import { useMutation, useQuery } from "convex/react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { api } from "../../convex/_generated/api";
import { pickFile } from "@/lib/xlsx";
import { Badge, Button, Card, Empty, Field, Input, Modal, PageHeader, Select, Spinner, fmtDate, fmtIdx, fmtNum, fmtPct, useRun } from "@/components/ui";

export function Projects() {
  const rows = useQuery(api.projects.portfolio);
  const me = useQuery(api.account.me);
  const [open, setOpen] = useState(false);
  const createDemo = useMutation(api.demo.createDemo);
  const [restore, setRestore] = useState<{ backup: { project: { code: string; name: string } }; code: string; name: string } | null>(null);
  const run = useRun();
  const [demoBusy, setDemoBusy] = useState(false);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [manager, setManager] = useState("");
  const filtered = useMemo(
    () =>
      (rows ?? []).filter(
        (r) =>
          (!q || `${r.code} ${r.name} ${r.client}`.toLowerCase().includes(q.toLowerCase())) &&
          (!status || r.status === status) &&
          (!manager || r.manager === manager),
      ),
    [rows, q, status, manager],
  );
  // V08: grouped and sorted by client, then project.
  const byClient = useMemo(() => {
    const m = new Map<string, typeof filtered>();
    for (const r of [...filtered].sort((a, b) => a.client.localeCompare(b.client) || a.code.localeCompare(b.code, undefined, { numeric: true }))) {
      m.set(r.client, [...(m.get(r.client) ?? []), r]);
    }
    return [...m.entries()];
  }, [filtered]);
  if (!rows) return <Spinner />;
  const managers = [...new Set(rows.map((r) => r.manager).filter(Boolean))] as string[];
  return (
    <div className="mx-auto max-w-7xl p-6">
      <PageHeader
        title="Projects"
        sub="Grouped by client. Figures are at each project's current cutoff, in hours."
        actions={
          me?.role !== "viewer" && (
            <>
              {!rows.some((r) => r.code === "DEMO-1") && (
                <Button
                  busy={demoBusy}
                  onClick={async () => {
                    setDemoBusy(true);
                    await run(() => createDemo({}), "Demo project created: 66 deliverables, 25 closed weeks. Baseline password: demo123");
                    setDemoBusy(false);
                  }}
                >
                  Load demo project
                </Button>
              )}
              {me?.role === "admin" && (
                <Button
                  onClick={async () => {
                    const f = await pickFile(".json");
                    if (!f) return;
                    try {
                      const backup = JSON.parse(await f.text());
                      if (backup?.format !== "evm-backup") throw new Error("not a backup");
                      setRestore({ backup, code: `${backup.project.code}-COPY`, name: backup.project.name });
                    } catch {
                      void run(async () => {
                        throw new Error(`${f.name} isn't an EVM project backup`);
                      });
                    }
                  }}
                >
                  Restore from backup
                </Button>
              )}
              <Button variant="primary" onClick={() => setOpen(true)}>
                <Plus className="size-4" /> New project
              </Button>
            </>
          )
        }
      />
      <div className="mb-3 flex flex-wrap gap-2">
        <Input placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} className="w-60" aria-label="Search projects" />
        <Select value={status} onChange={(e) => setStatus(e.target.value)} className="w-40" aria-label="Status filter">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="onhold">On hold</option>
          <option value="closed">Closed</option>
        </Select>
        <Select value={manager} onChange={(e) => setManager(e.target.value)} className="w-48" aria-label="Manager filter">
          <option value="">All managers</option>
          {managers.map((m) => (
            <option key={m}>{m}</option>
          ))}
        </Select>
      </div>
      {!rows.length ? (
        <Empty action={me?.role !== "viewer" && <Button variant="primary" onClick={() => setOpen(true)}>Create the first project</Button>}>No projects yet.</Empty>
      ) : (
        <div className="space-y-4">
          {byClient.map(([client, ps]) => (
            <Card key={client} title={client} bodyClass="p-0">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs text-slate-500">
                  <tr>
                    <th className="px-4 py-2">Project</th>
                    <th className="px-4 py-2">Manager</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2">Cutoff</th>
                    <th className="px-4 py-2 text-right">Deliverables</th>
                    <th className="px-4 py-2 text-right">Budget (h)</th>
                    <th className="px-4 py-2 text-right">Planned</th>
                    <th className="px-4 py-2 text-right">Earned</th>
                    <th className="px-4 py-2 text-right">SPI</th>
                    <th className="px-4 py-2 text-right">CPI</th>
                    <th className="px-4 py-2 text-right">EAC (h)</th>
                  </tr>
                </thead>
                <tbody>
                  {ps.map((r) => (
                    <tr key={r._id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-2">
                        <Link to="/p/$projectId" params={{ projectId: r._id }} className="font-medium text-brand-700 hover:underline">
                          {r.code}
                        </Link>{" "}
                        <span className="text-slate-600">{r.name}</span>
                      </td>
                      <td className="px-4 py-2">{r.manager}</td>
                      <td className="px-4 py-2">
                        <Badge tone={r.status === "active" ? "green" : r.status === "onhold" ? "amber" : "slate"}>{r.status === "onhold" ? "on hold" : r.status}</Badge>
                      </td>
                      <td className="px-4 py-2">{fmtDate(r.cutoff)}</td>
                      <td className="num px-4 py-2 text-right">{r.deliverables}</td>
                      <td className="num px-4 py-2 text-right">{fmtNum(r.metrics?.bac)}</td>
                      <td className="num px-4 py-2 text-right">{fmtPct(r.metrics?.pctPlanned)}</td>
                      <td className="num px-4 py-2 text-right">{fmtPct(r.metrics?.pctEarned)}</td>
                      <td className="num px-4 py-2 text-right">{fmtIdx(r.metrics?.spi)}</td>
                      <td className="num px-4 py-2 text-right">{fmtIdx(r.metrics?.cpi)}</td>
                      <td className="num px-4 py-2 text-right">{fmtNum(r.metrics?.eac1)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          ))}
        </div>
      )}
      <NewProject open={open} onClose={() => setOpen(false)} />
      {restore && <RestoreDialog init={restore} onClose={() => setRestore(null)} />}
    </div>
  );
}

function NewProject({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create = useMutation(api.projects.create);
  const run = useRun();
  const nav = useNavigate();
  const [f, setF] = useState({ code: "", name: "", client: "", manager: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    const id = await run(() => create(f));
    setBusy(false);
    if (id) {
      onClose();
      void nav({ to: "/p/$projectId/setup", params: { projectId: id } });
    }
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New project"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={busy} onClick={submit} disabled={!f.code || !f.name || !f.client}>
            Create
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="Project code">
          <Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} autoFocus />
        </Field>
        <Field label="Client">
          <Input value={f.client} onChange={(e) => setF({ ...f, client: e.target.value })} />
        </Field>
        <Field label="Name" className="col-span-2">
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        </Field>
        <Field label="Manager" className="col-span-2">
          <Input value={f.manager} onChange={(e) => setF({ ...f, manager: e.target.value })} />
        </Field>
      </div>
    </Modal>
  );
}

function RestoreDialog({ init, onClose }: { init: { backup: unknown; code: string; name: string }; onClose: () => void }) {
  const restoreBackup = useMutation(api.backup.restoreBackup);
  const run = useRun();
  const nav = useNavigate();
  const [f, setF] = useState({ code: init.code, name: init.name });
  const [busy, setBusy] = useState(false);
  return (
    <Modal
      open
      onClose={onClose}
      title="Restore project from backup"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            busy={busy}
            disabled={!f.code.trim()}
            onClick={async () => {
              setBusy(true);
              const id = await run(() => restoreBackup({ backup: init.backup, code: f.code, name: f.name }), "Project restored");
              setBusy(false);
              if (id) {
                onClose();
                void nav({ to: "/p/$projectId", params: { projectId: id } });
              }
            }}
          >
            Restore
          </Button>
        </>
      }
    >
      <p className="mb-3 text-sm text-slate-600">The backup is restored as a new project. Its baseline comes back unlocked; lock it again with a new password.</p>
      <div className="grid grid-cols-2 gap-3">
        <Field label="New project code">
          <Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} />
        </Field>
        <Field label="Name">
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        </Field>
      </div>
    </Modal>
  );
}
